import { useCallback, useState } from 'react';
import Toast from 'react-native-root-toast';

import { useUserContext } from '../../../../common/utils/context';

/**
 * Which tools the user shows on Home: the `homeTools` user setting (so the
 * choice follows them across devices), or defaultKeys until they edit it.
 * Ticks apply at once; the choice is saved when the edit sheet closes. A failed
 * save (e.g. offline) keeps the on-screen choice until Home is left (app closed
 * or signed out).
 * Returns { shownKeys, setToolShown(key, isShown), saveShownKeys() }.
 */
const useHomeToolKeys = (defaultKeys) => {
	const { userSettings, modifyUserSetting } = useUserContext();
	// Set on the first tick; null while the saved setting or the defaults apply.
	const [editedKeys, setEditedKeys] = useState(null);
	const savedKeys = Array.isArray(userSettings?.homeTools) ? userSettings.homeTools : null;
	const shownKeys = editedKeys || savedKeys || defaultKeys;

	// Keys of tools the user cannot open right now stay in the list, so a tool
	// comes back if access returns.
	const setToolShown = useCallback((key, isShown) => {
		setEditedKeys(current => {
			const keys = (current || savedKeys || defaultKeys).filter(shownKey => shownKey !== key);
			return isShown ? [...keys, key] : keys;
		});
	}, [defaultKeys, savedKeys]);

	const saveShownKeys = useCallback(() => {
		// Saves on every close after an edit: skipping "unchanged" lists could drop
		// an undo made while the previous save was still in flight.
		if (!editedKeys) {
			return;
		}
		modifyUserSetting({ homeTools: editedKeys }).catch(() => {
			Toast.show('Your tools could not be saved. Check your connection.');
		});
	}, [editedKeys, modifyUserSetting]);

	return { shownKeys, setToolShown, saveShownKeys };
};

export default useHomeToolKeys;
